'use client';

import { LayoutContainer, Section, TextPassage } from '@petsmart-ui/sparky';
import cn from 'classnames';
import { usePathname } from 'next/navigation';
import type { FC } from 'react';

import { ContentfulClick, trackTile6UpClick, trackTilesClicks } from '@libs/analytics';

import { COLOR_TABLE } from '../../constants';
import type { Tile6UpVariant } from '../../types';
import { getPlpModuleSectionHeading, getTrimmedDisplayTitle } from '../../utils/plpModuleSectionHeading';
import { ColoredTextLink } from '../ColoredTextLink';
import Tile6UpCampaign from './Tile6UpCampaign';
import Tile6UpMultirow from './Tile6UpMultirow';
import Tile6UpPromo from './Tile6UpPromo';
import Tile6UpShoppable from './Tile6UpShoppable';
import Tile6UpTips from './Tile6UpTips';
import Tile6UpVisualNav from './Tile6UpVisualNav';
import type { Tile6UpProps } from './types';
import './Tile6Up.scss';

const ComponentTile6Up: Record<Tile6UpVariant, FC<Tile6UpProps>> = {
    Multirow: Tile6UpMultirow,
    Promo: Tile6UpPromo,
    Shoppable: Tile6UpShoppable,
    VisualNav: Tile6UpVisualNav,
    Tips: Tile6UpTips,
    Campaign: Tile6UpCampaign,
};

export default function Tile6Up({
    variant,
    header,
    backgroundColor,
    textColor,
    linkAction,
    linkActionColor,
    tiles,
    description,
    alignText,
    isH1,
}: Readonly<Tile6UpProps>) {
    const Component = ComponentTile6Up[variant];
    const pathname = usePathname();

    // To avoid rendering the component if there are no tiles to show
    if (tiles?.length === 0) {
        return null;
    }

    const isCentered = alignText !== 'left';
    const { link, actionText } = linkAction || {};
    const displayHeader = getTrimmedDisplayTitle(header);
    const { title: sectionTitle, headingTagName } = getPlpModuleSectionHeading(header, isH1);

    const restyledVariants = ['Multirow', 'Promo', 'Shoppable', 'Tips', 'Campaign'];
    const isRestyled = restyledVariants.includes(variant);
    const variantLowercase = variant.toLowerCase();
    const paddedContainerClassName = cn({ 'tile6up-container--no-bg': variant === 'Campaign' && !backgroundColor });

    const componentClassName = cn(
        `tile6up variant-${variantLowercase}`,
        !displayHeader && 'no-tile-header',
        isCentered && 'title-center',
        isRestyled && `tile6up--restyled`
    );

    return (
        <LayoutContainer
            className={`tile6up-container tile6up-container--${variant.toLowerCase()} ${paddedContainerClassName}`}
        >
            <Section
                data-testid="tile6up-test-id"
                style={{
                    ...(variant === 'Campaign' && { backgroundColor, color: textColor && COLOR_TABLE[textColor] }),
                }}
                title={sectionTitle}
                headingTagName={headingTagName}
                description={!isCentered ? description : ''}
                {...(isRestyled && {
                    align: isCentered ? 'center' : undefined,
                    titleAfterLocation: 'below-description',
                })}
                titleAfter={
                    <>
                        {isCentered && description && (
                            <TextPassage
                                style={{
                                    ...(variant === 'Campaign' && { color: textColor && COLOR_TABLE[textColor] }),
                                }}
                            >
                                {description}
                            </TextPassage>
                        )}
                        {link && actionText && (
                            <ContentfulClick
                                eventData={{
                                    linkText: actionText ?? '',
                                    linkUrl: link.webUrl ?? '',
                                    promoTitle: header ?? '',
                                    promoVariant: `tile 6up - ${variantLowercase}`,
                                    promoDesc: link.internalTitle ?? '',
                                    promoType: `tile-6up-${variantLowercase}`,
                                }}
                            >
                                <ColoredTextLink
                                    data-testid="tile6up-link-action"
                                    href={link?.webUrl}
                                    onClick={() => {
                                        trackTile6UpClick({
                                            variant,
                                            name: actionText,
                                            position: null,
                                            navigationClick: null,
                                        });

                                        if (variant === 'Multirow') {
                                            trackTilesClicks({
                                                variant,
                                                header: header ?? '',
                                                title: actionText,
                                                position: -1,
                                                row: -1,
                                            });
                                        }
                                    }}
                                    color={linkActionColor}
                                >
                                    {actionText}
                                </ColoredTextLink>
                            </ContentfulClick>
                        )}
                    </>
                }
                className={componentClassName}
            >
                <Component
                    variant={variant}
                    header={header}
                    linkAction={linkAction}
                    tiles={tiles}
                    pathname={pathname || ''}
                    textColor={textColor}
                />
            </Section>
        </LayoutContainer>
    );
}
